import type { CreateIntegrationOnboardingSessionResponse } from '@heidi/integrations/connected-apps/contracts';
import type { EHRProvider } from '@heidi/schema/ml-scribe';

/**
 * Latest completed onboarding session, newest by `updated_at`.
 *
 * Scoped to `provider` when one is supplied (the ACTIVE session's provider, so
 * a multi-integration user's editor binds to the integration they are working
 * in) and otherwise the newest completed session of any provider.
 *
 * DELIBERATELY NOT provider-filtered to athenahealth. The backend reads the
 * stored `template_editor_version` off the session with no provider check, so a
 * pin is honored for every integration; only the flag/date FALLBACK is
 * provider-specific. Filtering to one vendor here would silently discard a
 * valid decision for every other integration.
 *
 * Single shared selector so every editor-version consumer picks the same
 * session document and the pick cannot drift between them.
 *
 * A missing or unparseable `updated_at` is treated as epoch 0 (oldest) rather
 * than letting `new Date(...).getTime()` return `NaN`, which makes the sort
 * comparator return `NaN` and the "newest" pick implementation-defined. A
 * wrong pick is now a wrong editor (the stored pin of whichever session the
 * sort happened to surface), so the guard is load-bearing where it used to be
 * cosmetic behind `resolveCutover`.
 */
const safeUpdatedTime = (s: CreateIntegrationOnboardingSessionResponse): number => {
  const t = new Date(s.updated_at).getTime();
  return Number.isFinite(t) ? t : 0;
};

export const selectLatestOnboardingSession = (
  sessions: CreateIntegrationOnboardingSessionResponse[],
  provider?: EHRProvider
): CreateIntegrationOnboardingSessionResponse | undefined =>
  sessions
    .filter((s) => !provider || (s.provider as EHRProvider) === provider)
    .sort((a, b) => safeUpdatedTime(b) - safeUpdatedTime(a))[0];
